/**
 * @file Allows users to upload files to Azure Blob Storage.
 */
import React, { ChangeEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { t } from "i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import { PauseIcon, PlayIcon, UploadIcon } from "../common/ProjectIcons";
import { BeatLoader } from "react-spinners";
import { useUpload } from "./UploadContext";
import AzureUploadManager from "./AzureUploadManager";
import { getSasUrlForUpload } from "../../api/uploadApi";
import {
  endDeploymentUploadWithRetry,
  EndUploadOutcome,
} from "../../api/deploymentsApi";
import { DeploymentStatus } from "../common/ObjectTypes";
import {
  clearInterruptedUpload,
  InterruptedUpload,
  readInterruptedUpload,
} from "./interruptedUpload";
import {
  findFlattenedNameCollisions,
  FlattenedNameCollision,
} from "../common/helpers";

/** Clashing names listed before the rest are summarised as "…and N more" (AKL-588). */
const MAX_COLLISIONS_SHOWN = 20;

interface UploadComponentProps {
  projectId: number;
  projectGuid: string;
  deploymentId: number;
  deploymentName: string;
  deploymentGuid: string;
  fetchUpdate: () => Promise<void>;
  canUpload: boolean;
}

declare module "react" {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string;
  }
}

// Recessed-panel wrapper shared by the idle drop-zone and the uploading box.
const wellBox =
  "rounded-[10px] border border-off-white-dark dark:border-storm-blue-border bg-off-white-dark/40 dark:bg-storm-blue-well px-6 py-5 transition duration-300";
// Small stat tile used inside the uploading box (Selected/Uploaded/Skipped/Remaining).
const statTile =
  "rounded-lg border border-off-white-dark dark:border-storm-blue-border bg-white dark:bg-storm-blue-card px-3.5 py-3 transition duration-300";
const statTileLabel =
  "text-[10.5px] font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text transition duration-300";

/**
 * HTTP status from a rejected one-shot `end_upload`, or undefined when the server never
 * answered. The interceptor hands back its normalized `{ message, status, data }` for a
 * 403 or 5xx (AKL-895), so `status` is read first; `response.status` covers a raw axios
 * error should the call ever bypass the interceptor altogether (the `statusOf` shape
 * feedbackApi and exportsApi each keep).
 * @param err whatever the call rejected with
 */
const statusOf = (err: unknown): number | undefined => {
  const shaped = err as {
    status?: number;
    response?: { status?: number };
  } | null;
  return shaped?.status ?? shaped?.response?.status;
};

/**
 * UploadComponent is a React functional component that handles the file upload process.
 * It allows users to select a folder of files, start the upload, pause/resume the upload, and cancel the upload.
 * The component uses the `useUpload` hook to manage the upload state and progress. When a
 * previous upload to this deployment was lost to an expired Keycloak session, the idle
 * panel says how much of it was sent (AKL-891). Success is claimed only once the server
 * has recorded the end of the upload; a record refused because another user took the
 * upload lock over is explained as such (AKL-895). The click that opens the folder picker
 * asks for the screen wake lock, since Safari grants it only in a user gesture (AKL-1088).
 *
 * @component
 * @param {UploadComponentProps} props - The properties passed to the component.
 * @param {number} props.projectId - The ID of the project to which the files are being uploaded.
 * @param {string} props.projectGuid - The GUID of the project to which the files are being uploaded.
 * @param {number} props.deploymentId - The ID of the deployment to which the files are being uploaded.
 * @param {string} props.deploymentName - The name of the deployment to which the files are being uploaded.
 * @param {string} props.deploymentGuid - The GUID of the deployment to which the files are being uploaded.
 * @param {() => Promise<void>} props.fetchUpdate - A function to fetch updates after the upload is complete.
 * @param {boolean} props.canUpload - A boolean indicating whether the user has permission to upload files.
 *
 * @returns {JSX.Element} The rendered UploadComponent.
 */
const UploadComponent: React.FC<UploadComponentProps> = ({
  projectId,
  projectGuid,
  deploymentId,
  deploymentName,
  deploymentGuid,
  fetchUpdate,
  canUpload,
}) => {
  const {
    deploymentId: uploadDeploymentId,
    inProgress,
    uploadIsPaused: isPaused,
    uploadIsStopped: isStopped,
    failedFileCount,
    uploadAccepted,
    retryStatus,
    selectedFiles,
    uploadProgress,
    skippedFileCount,
    uploadedFileCount,
    startUpload,
    pauseUpload,
    resumeUpload,
    cancelUpload,
    failUpload,
    endUpload,
    requestScreenWakeLock,
    releaseUnusedScreenWakeLock,
  } = useUpload();
  const [retrievingSasUrl, setRetrievingSasUrl] = useState(false);
  // The folder input, held as state (a callback ref) so the effect below can listen to it.
  const [folderInput, setFolderInput] = useState<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>(selectedFiles);
  const [interruptedUpload, setInterruptedUpload] =
    useState<InterruptedUpload | null>(null);
  // The clashing names of the folder the user just chose (AKL-588), with the deployment it
  // was chosen for; null when there are none.
  const [collisions, setCollisions] = useState<{
    deploymentId: number;
    list: FlattenedNameCollision[];
  } | null>(null);

  const isUploadingHere = inProgress && uploadDeploymentId === deploymentId;

  /**
   * Looks for an upload to this deployment that a dead Keycloak session cut short
   * (AKL-891). Storage is read here, not during render; the record is left in place until
   * the user dismisses the notice or starts a new upload on this deployment.
   */
  useEffect(() => {
    setInterruptedUpload(readInterruptedUpload(deploymentId));
  }, [deploymentId]);

  // A refused folder belongs to the deployment it was chosen for: switching deployment in
  // the dropdown clears its collision list (AKL-588).
  useEffect(() => {
    setCollisions(null);
  }, [deploymentId]);

  /**
   * A dismissed folder picker starts no upload, so it lets go of the screen wake lock its
   * click asked for (AKL-1088). The input's `cancel` event is listened to directly: React
   * 18 wires `onCancel` only for `<dialog>`.
   */
  useEffect(() => {
    if (!folderInput) return;
    folderInput.addEventListener("cancel", releaseUnusedScreenWakeLock);
    return () =>
      folderInput.removeEventListener("cancel", releaseUnusedScreenWakeLock);
  }, [folderInput, releaseUnusedScreenWakeLock]);

  // The deployment match matters (review cycle 5): when the dropdown changes `deploymentId`,
  // the state still holds the previous deployment's record until the effect above runs,
  // and a notice rendered from it would be announced under the wrong deployment.
  const showInterruptedUploadNotice =
    interruptedUpload !== null &&
    interruptedUpload.deploymentId === deploymentId;

  // The same guard for a refused folder (AKL-588, review cycle 1): until the effect above
  // clears it, the state still holds the previous deployment's list.
  const shownCollisions =
    collisions !== null && collisions.deploymentId === deploymentId
      ? collisions.list
      : [];

  /**
   * Forgets the interrupted upload this deployment's notice is about — on Dismiss, and
   * when a new upload starts here, which is what the notice asked for. Only this
   * deployment's record goes; the same account's records for other deployments stay. Not
   * guarded on the notice being shown (review cycle 5): a record another tab wrote after
   * this panel read storage, or one the read effect has not caught up with yet, must not
   * survive a new upload to reappear afterwards — removing a key that is not there costs
   * nothing.
   */
  const forgetInterruptedUpload = () => {
    clearInterruptedUpload(deploymentId);
    setInterruptedUpload(null);
  };

  /**
   * Keep the local `files` state aligned with the upload that is in progress
   * for the currently displayed deployment. Hydrates from `selectedFiles` when
   * the in-progress upload belongs to this deployment, otherwise clears local
   * state. This runs on dropdown-driven `deploymentId` changes so the progress
   * UI re-appears when the user returns to an upload-in-progress deployment.
   */
  useEffect(() => {
    if (isUploadingHere && selectedFiles.length > 0) {
      setFiles(selectedFiles);
    } else {
      setFiles([]);
    }
  }, [deploymentId, isUploadingHere, selectedFiles]);

  /**
   * Asynchronously retrieves a SAS (Shared Access Signature) URL for the specified project.
   *
   * This function sets the `retrievingSasUrl` state to `true` before making the request
   * and sets it back to `false` once the request has settled — on a rejection too, since
   * the manager now stops the upload on a failed fetch and the idle panel must not come
   * back with its button stuck as a spinner (AKL-889).
   *
   * @returns {string | null} The SAS URL for the project.
   */
  const handleRetrieveSasUrl = async () => {
    setRetrievingSasUrl(true);
    try {
      return await getSasUrlForUpload(projectId, projectGuid);
    } finally {
      setRetrievingSasUrl(false);
    }
  };

  /**
   * Handles the upload started event.
   * Refreshes the History table to show the new upload record.
   */
  const handleUploadStarted = async () => {
    // Trigger event refresh to show the new History entry
    await fetchUpdate();
  };

  /**
   * Tells the user that another user took the deployment's upload lock over
   * while this browser was away (AKL-895) — the server refused the record with
   * a 403. The files that were sent are on the server; only the History entry
   * is not this client's to write. The ticket's sentence, with its "Your files
   * were uploaded", is right for a completion; after a Cancel the user has just
   * stopped an upload with files remaining (and, from the stopped state, has
   * just been told it failed), so that clause is dropped. 10 s, as the
   * session-expired toast: two sentences to read.
   *
   * @param status the status whose record was refused
   */
  const explainLockTakeover = (status: DeploymentStatus) => {
    toast.error(
      status === DeploymentStatus.COMPLETED
        ? t(
            "Another user took over uploading to this deployment while you were away. Your files were uploaded; the History entry now belongs to them.",
          )
        : t(
            "Another user took over uploading to this deployment while you were away. The History entry now belongs to them.",
          ),
      { autoClose: 10000 },
    );
  };

  /**
   * Explains how the final status of an upload was resolved and refreshes the
   * History where there is something new to show. The request was retried
   * across a network gap (AKL-890); when the server still did not take it, the
   * History has nothing new and the user is told why instead: the session
   * expired (a 401 — the same expired token would only fail the refresh too),
   * or the result could not be recorded within the retry schedule. A refusal
   * (AKL-895) is different: the server answered, and another user's takeover
   * wrote a History row this browser has not seen — so the takeover is
   * explained and the History refreshed to show that row.
   *
   * @param outcome how `endDeploymentUploadWithRetry` resolved
   * @param status the status that was being recorded
   */
  const reportUploadEndOutcome = async (
    outcome: EndUploadOutcome,
    status: DeploymentStatus,
  ) => {
    if (outcome === "unauthorized") {
      // 10 s rather than the default 5 s, as the Report this toast does: two
      // sentences the user has to act on.
      toast.error(
        t(
          "Your session expired before the upload was recorded. Sign in again and upload the same folder to send any missing files.",
        ),
        { autoClose: 10000 },
      );
      return;
    }
    if (outcome === "failed") {
      toast.error(
        t(
          "akili could not record the result of this upload. Check this deployment's history later.",
        ),
      );
      return;
    }
    if (outcome === "refused") {
      explainLockTakeover(status);
    }
    // trigger event refresh
    await fetchUpdate();
  };

  /**
   * Records the final status of this deployment's upload through the retrying
   * helper (AKL-890). The outcome is returned, not reported here, so the
   * completion path can claim success between the record landing and the
   * report (see `reportUploadEndOutcome`).
   *
   * @param status the final status to record
   * @param uploadedFileCount the number of files uploaded in this run
   * @returns how the record was resolved
   */
  const recordUploadEnd = (
    status: DeploymentStatus,
    uploadedFileCount: number,
  ): Promise<EndUploadOutcome> =>
    endDeploymentUploadWithRetry(deploymentId, {
      status,
      file_count: uploadedFileCount,
    });

  /**
   * Records the completion, then — only once the server has taken the record
   * — says the files were uploaded (AKL-895). The toast used to come first, so
   * a record that was refused or never landed followed a success message; now
   * that outcome's own message is all the user sees (see
   * `reportUploadEndOutcome`).
   */
  const handleUploadComplete = async (uploadedFileCount: number) => {
    const outcome = await recordUploadEnd(
      DeploymentStatus.COMPLETED,
      uploadedFileCount,
    );
    if (outcome === "recorded") {
      toast.success(
        t(
          "{{numberFiles}} files successfully uploaded for deployment {{name}}",
          {
            numberFiles: uploadedFileCount,
            name: deploymentName,
          },
        ),
      );
    }
    await reportUploadEndOutcome(outcome, DeploymentStatus.COMPLETED);
  };

  /**
   * Displays a failure message, tears the upload state down so the failed run
   * does not reappear on this deployment, then records the failure (see
   * `recordUploadEnd`). Since AKL-889 the manager never ends a batch for a
   * failed file — it stops and waits for Resume or Cancel — so nothing calls
   * this in practice; it stays wired so an unforeseen call is never swallowed.
   */
  const handleUploadFailed = async (uploadedFileCount: number) => {
    toast.error(
      t("Upload to {{name}} has failed.", {
        name: deploymentName,
      }),
    );
    setFiles([]);
    // Tear down upload context so a failed upload's state doesn't reappear
    // when the user navigates back to this deployment via the dropdown.
    endUpload();
    const outcome = await recordUploadEnd(
      DeploymentStatus.FAILED,
      uploadedFileCount,
    );
    await reportUploadEndOutcome(outcome, DeploymentStatus.FAILED);
  };

  /**
   * Cancels the upload. From the stopped state this is the final give-up
   * (AKL-889): the user is told the upload failed, the panel is reset, and the
   * manager records FAILED with the honest uploaded count through the retrying
   * helper — the outcome is explained the same way as a completion's. While
   * running or paused, Cancel keeps its ordinary path: CANCELLED, one shot.
   *
   * That one-shot record no longer signs the user out or redirects when it
   * fails (AKL-895 — see `endDeploymentUpload`), so its rejection is explained
   * here instead: a 403 is the lock taken over by another user (the History is
   * refreshed to show their row); any other server answer gets the
   * could-not-record message; a rejection with no status is a network failure
   * or timeout the interceptor has already toasted, a Keycloak refresh that
   * could not run, or the interceptor's `UnauthorizedError` for a 401 — the
   * session is gone and the next request signs the user out, as for any other
   * call — and nothing is added. The provider has already reset the panel
   * before the request went out, so the panel is right in every case.
   *
   * An upload that stopped before the server accepted it (AKL-823) has already been
   * explained by the manager's "Upload could not start" toast and has no run to close, so
   * its Cancel only clears the panel: no "has failed" toast, and `failUpload` sends nothing.
   */
  const handleCancelUpload = async () => {
    setFiles([]);
    if (isUploadingHere && isStopped) {
      if (uploadAccepted) {
        toast.error(
          t("Upload to {{name}} has failed.", {
            name: deploymentName,
          }),
        );
      }
      const outcome = await failUpload();
      if (outcome) {
        await reportUploadEndOutcome(outcome, DeploymentStatus.FAILED);
      }
      return;
    }
    if (isUploadingHere) {
      try {
        await cancelUpload();
      } catch (err) {
        // Already logged where it was thrown (`endDeploymentUpload`).
        const status = statusOf(err);
        if (status !== 403) {
          if (status !== undefined) {
            toast.error(
              t(
                "akili could not record the result of this upload. Check this deployment's history later.",
              ),
            );
          }
          return;
        }
        explainLockTakeover(DeploymentStatus.CANCELLED);
      }
    }
    // trigger event refresh
    await fetchUpdate();
  };

  /**
   * Initiates the upload process by canceling any ongoing uploads, retrieving previously uploaded file hashes,
   * and starting a new upload using the AzureUploadManager.
   *
   * @returns {Promise<void>} A promise that resolves when the upload process is initiated.
   */
  const handleStartUpload = (files: File[]) => {
    endUpload();
    forgetInterruptedUpload();
    const manager = new AzureUploadManager({
      projectId,
      projectGuid,
      deploymentId,
      deploymentName,
      deploymentGuid,
      selectedFiles: files,
      retrieveSasUrl: handleRetrieveSasUrl,
      onUploadStarted: handleUploadStarted,
      onUploadComplete: handleUploadComplete,
      onUploadFailed: handleUploadFailed,
    });
    startUpload(manager);
  };

  /**
   * The click that opens the folder picker is the user gesture an upload starts from, so
   * the screen wake lock is asked for here, synchronously and before the picker opens
   * (AKL-1088). Safari grants it only with user activation, and the picker's `change`,
   * where the upload actually starts, has none. The lock is kept for that upload.
   */
  const handleFolderInputClick = () => {
    requestScreenWakeLock();
  };

  /**
   * Handle the file selection event and store the selected files in the state. A
   * selection that starts no upload — nothing chosen, or a folder refused for clashing
   * names — lets go of the screen wake lock the picker's click asked for (AKL-1088).
   * @param {ChangeEvent<HTMLInputElement>} event - The file selection event.
   */
  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    // Snapshot the selection before clearing the input: `event.target.files`
    // is a live FileList that empties when the value is reset.
    const selected = Array.from(event.target.files ?? []);
    // Reset the input value to allow re-selection of the same folder. This has
    // to happen on the cancelled path too, or picking the same folder again
    // fires no change event at all.
    event.target.value = "";
    // Cancelling the browser's "Upload N files to this site?" confirmation
    // still fires a change event, just with an empty list. An empty FileList
    // is truthy, so the count is what decides whether an upload starts —
    // otherwise a cancelled selection registers an upload for 0 files and
    // completes it immediately.
    if (selected.length === 0) {
      releaseUnusedScreenWakeLock();
      return;
    }
    // Two files that would be stored under one name lose one of them (AKL-588): refuse the
    // folder before anything is sent — no start_upload, no History row — and say which
    // files clash. A clean folder clears the list of an earlier refused one.
    const clashes = findFlattenedNameCollisions(selected);
    setCollisions(clashes.length > 0 ? { deploymentId, list: clashes } : null);
    if (clashes.length > 0) {
      releaseUnusedScreenWakeLock();
      return;
    }
    setFiles(selected);
    handleStartUpload(selected);
  };

  const remaining =
    selectedFiles.length - (uploadedFileCount + skippedFileCount);

  // Between clicking "Upload folder" and the first file actually being
  // handled, the manager is still fetching a SAS URL and registering the
  // upload session with the backend — progress stays at 0 with nothing
  // handled yet, which otherwise looks stalled rather than starting up.
  const isPreparingUpload =
    isUploadingHere &&
    !isPaused &&
    !isStopped &&
    uploadedFileCount + skippedFileCount === 0 &&
    uploadProgress === 0;

  // The Pause/Resume button reads Resume whenever a click on it would continue
  // the upload: paused by the user, or stopped by the manager (AKL-889).
  const canResume = isPaused || isStopped;

  /**
   * The box's title for the current state (AKL-889 adds "Upload stopped").
   */
  const uploadTitle = () => {
    if (isStopped) return t("Upload stopped");
    if (isPaused) return t("Upload paused");
    if (isPreparingUpload) return t("Preparing upload...");
    return t("Uploading files");
  };

  /**
   * The box's line under the title. Stopped: how many files were not uploaded
   * and what to do about it; that the upload never started, when the server
   * did not accept it (AKL-823); or a plain resume hint when the pass stopped
   * before any file was given up. Paused and preparing: as before. Uploading:
   * the reconnecting or retrying state when the manager is between attempts
   * (AKL-889), otherwise what the user must do for the upload to finish — the
   * old "continues in the background" promise was not something the browser
   * can keep.
   */
  const uploadSubtitle = () => {
    if (isStopped) {
      if (failedFileCount === 1) {
        // A single file failing is an ordinary outcome, so it gets its own sentence
        // rather than "1 files were not uploaded" (review cycle 5). The catalogs carry
        // no i18next plural forms and nothing else in the app uses them, so this is an
        // explicit branch rather than a `_one`/`_other` pair.
        return t(
          "One file was not uploaded. Resume to try again, or upload the same folder later to send only the missing file.",
        );
      }
      return failedFileCount > 0
        ? t(
            "{{count}} files were not uploaded. Resume to try again, or upload the same folder later to send only the missing files.",
            { count: failedFileCount },
          )
        : uploadAccepted
          ? t("Resume to continue where it left off.")
          : t("The upload could not start. Resume to try again.");
    }
    if (isPaused) return t("Resume to continue where it left off.");
    if (isPreparingUpload) return t("Getting ready to upload your files.");
    if (retryStatus.kind === "waitingForNetwork") {
      return t("Connection lost. Waiting to reconnect…");
    }
    if (retryStatus.kind === "retrying") {
      return t("Retrying {{filename}} ({{attempt}} of {{max}})", {
        filename: retryStatus.filename,
        attempt: retryStatus.attempt,
        max: retryStatus.maxAttempts,
      });
    }
    return t(
      "You can use other pages in akili while this runs. Keep this tab open and the computer awake until it finishes.",
    );
  };

  if (inProgress && !isUploadingHere) {
    return (
      <p className={`mt-2 ${tailwindClassnames.helperText}`}>
        {t("Upload in progress for another deployment.")}
      </p>
    );
  }

  if (isUploadingHere) {
    return (
      <div className={`mt-2 ${wellBox}`}>
        <div className="flex flex-wrap items-start justify-between gap-5">
          {/* The title and its line change on their own while the user is elsewhere on
              the page — offline, retrying, stopped — so they are announced rather than
              only shown (aria-live as ExportPanel and TransferDownloadPage use it). The
              counts and the bar are outside it: those change on every file and would be
              a stream of noise. */}
          <div aria-live="polite">
            <div className="flex items-center gap-2.5">
              {isPreparingUpload ? (
                <BeatLoader
                  color={tailwindClassnames.skyBlueDark}
                  loading={true}
                  size={6}
                  margin={2}
                />
              ) : (
                <span
                  className={`h-2 w-2 rounded-full ${
                    isStopped
                      ? "bg-primary-orange dark:bg-primary-orange-light"
                      : "bg-sky-blue"
                  }`}
                />
              )}
              <span className="text-[15px] font-semibold text-primary-off-black dark:text-primary-off-white">
                {uploadTitle()}
              </span>
            </div>
            <div className="mt-1.5 text-[12.5px] text-off-black-light dark:text-greyed-text">
              {uploadSubtitle()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => (canResume ? resumeUpload() : pauseUpload())}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sky-blue-dark/30 bg-sky-blue/20 px-3.5 py-2 text-sm font-semibold text-primary-blue-dark transition duration-150 hover:bg-sky-blue/30 dark:border-sky-blue/30 dark:bg-sky-blue/15 dark:text-sky-blue-light dark:hover:bg-sky-blue/25"
            >
              {canResume ? <PlayIcon /> : <PauseIcon />}
              {canResume ? t("Resume") : t("Pause")}
            </button>
            <button
              onClick={() => handleCancelUpload()}
              className="whitespace-nowrap rounded-lg border border-primary-orange/40 bg-primary-orange/10 px-3.5 py-2 text-sm font-semibold text-primary-orange-dark transition duration-150 hover:bg-primary-orange/20 dark:text-primary-orange-light"
            >
              {t("Cancel upload")}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3.5">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-off-white-dark dark:bg-storm-blue">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary-blue-light to-sky-blue transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <div className="min-w-[46px] text-right text-sm font-semibold text-primary-off-black dark:text-primary-off-white">
            {Math.round(uploadProgress) || 0}%
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className={statTile}>
            <div className={statTileLabel}>{t("Selected")}</div>
            <div className="mt-0.5 text-xl font-semibold text-primary-off-black dark:text-primary-off-white">
              {files.length.toLocaleString()}
            </div>
          </div>
          <div className={statTile}>
            <div className={statTileLabel}>{t("Uploaded")}</div>
            <div className="mt-0.5 text-xl font-semibold text-primary-green dark:text-primary-green-light">
              {uploadedFileCount.toLocaleString()}
            </div>
          </div>
          <div className={statTile}>
            <div className={statTileLabel}>{t("Skipped")}</div>
            <div className="mt-0.5 text-xl font-semibold text-off-black-light dark:text-greyed-text">
              {skippedFileCount.toLocaleString()}
            </div>
          </div>
          <div className={statTile}>
            <div className={statTileLabel}>{t("Remaining")}</div>
            <div className="mt-0.5 text-xl font-semibold text-primary-off-black dark:text-primary-off-white">
              {remaining.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showInterruptedUploadNotice && (
        // The user has just come back from the sign-in page, so this is announced. It sits
        // above the idle panel because the panel is where "upload the same folder again"
        // happens; the record stays until Dismiss or that upload starts.
        <div
          role="status"
          className="mt-2 flex flex-wrap items-start justify-between gap-3 rounded-[10px] border border-primary-orange/40 bg-primary-orange/10 px-4 py-3 text-[12.5px] text-primary-orange-dark transition duration-300 dark:text-primary-orange-light"
        >
          <span className="flex-1 min-w-[12rem]">
            {t(
              "Your session expired while an upload was running. {{sent}} of {{selected}} files were sent; upload the same folder again to send the rest.",
              {
                sent: interruptedUpload.sentFileCount,
                selected: interruptedUpload.selectedFileCount,
              },
            )}
          </span>
          <button
            type="button"
            onClick={forgetInterruptedUpload}
            className="rounded-md px-2 py-1 text-[12.5px] font-semibold underline transition duration-150 hover:bg-primary-orange/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-orange/60"
          >
            {t("Dismiss")}
          </button>
        </div>
      )}
      {shownCollisions.length > 0 && (
        <div
          role="alert"
          data-testid="upload-name-collisions"
          className="mt-2 rounded-[10px] border border-primary-orange/40 bg-primary-orange/10 px-4 py-3"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className={tailwindClassnames.warningHeader}>
              {t("Name collisions detected")}
            </p>
            <button
              type="button"
              onClick={() => setCollisions(null)}
              className="rounded-md px-2 py-1 text-[12.5px] font-semibold text-primary-orange-dark underline transition duration-150 hover:bg-primary-orange/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-orange/60 dark:text-primary-orange-light"
            >
              {t("Dismiss")}
            </button>
          </div>
          <p className={`mt-1.5 ${tailwindClassnames.helperText}`}>
            {t(
              "These files would be stored under the same name in akili (upper and lower case count as the same), so one would replace the other. Nothing was uploaded. Rename the conflicting folders or files, then choose the folder again.",
            )}
          </p>
          <ul className="mt-2 space-y-2">
            {shownCollisions.slice(0, MAX_COLLISIONS_SHOWN).map((collision) => (
              <li
                key={collision.name.toLowerCase()}
                className={tailwindClassnames.helperText}
              >
                <span className="font-mono font-semibold">
                  {collision.name}
                </span>
                <ul className="mt-0.5 list-disc pl-5">
                  {collision.paths.map((path) => (
                    <li key={path} className="break-all">
                      {path}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {shownCollisions.length > MAX_COLLISIONS_SHOWN && (
            <p className={`mt-2 ${tailwindClassnames.helperText}`}>
              {t("…and {{count}} more", {
                count: shownCollisions.length - MAX_COLLISIONS_SHOWN,
              })}
            </p>
          )}
        </div>
      )}
      <div
        className={`mt-2 flex flex-wrap items-center justify-between gap-5 ${wellBox}`}
      >
        <div>
          <div className="text-[15px] font-semibold text-primary-off-black dark:text-primary-off-white">
            {t("No upload in progress")}
          </div>
          <div className="mt-1.5 text-[12.5px] text-off-black-light dark:text-greyed-text">
            {t("Choose a folder of images to upload to this deployment.")}
          </div>
        </div>
        {retrievingSasUrl ? (
          <button className={tailwindClassnames.btnPrimary} disabled>
            <BeatLoader
              color={tailwindClassnames.stormBlue}
              loading={true}
              size={9}
            />
          </button>
        ) : (
          <div>
            <label
              htmlFor="folderInput"
              className={
                canUpload
                  ? `${tailwindClassnames.btnPrimary} inline-flex items-center gap-2 hover:cursor-pointer`
                  : `${tailwindClassnames.btnPrimary} inline-flex cursor-not-allowed items-center gap-2 pointer-events-none opacity-45`
              }
            >
              <UploadIcon />
              {t("Upload folder")}
            </label>
            <input
              id="folderInput"
              ref={setFolderInput}
              type="file"
              webkitdirectory=""
              onClick={handleFolderInputClick}
              onChange={handleFileSelect}
              className="hidden"
              disabled={!canUpload}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default UploadComponent;
